/**
 * Integrate with Sentry.io
 *
 * This should be the first import of `index.ts` thus the `Aaa` prefix.
 */
import * as Sentry from '@sentry/nuxt';
import type { ErrorTag, HekateFormSubmit } from '@onshape/www-inertia';

/** Wrapper class for Sentry.io
 *
 * If you change the shape of the methods here, be sure to change them in hekate-www/shims-vue.d.ts
 */
class ErrorReporter {
  private hekateSubmitId: string = '';

  constructor() {
    this.setHekateSubmitIdListener();
  }

  public postToSentry = ({ tag, msg, error }: { tag: ErrorTag; msg: string; error?: Error }) => {
    console.error(`Posting to sentry - msg:${msg} www-tag:${tag}`);

    if (!error) {
      Sentry.withScope((scope) => {
        scope.setFingerprint([tag, msg]);
        scope.setTag('www-tag', tag);
        if (this.hekateSubmitId) scope.setTag('hekate-submit-id', this.hekateSubmitId);
        scope.setLevel('log');
        Sentry.captureMessage(`${tag} - ${msg}`);
      });
      return;
    }
    setTimeout(() => {
      Sentry.withScope((scope) => {
        scope.setFingerprint([tag, msg]);
        scope.setTag('www-tag', tag);
        if (this.hekateSubmitId) scope.setTag('hekate-submit-id', this.hekateSubmitId);
        scope.setLevel('error');
        Sentry.captureException({ tag, msg, error });
      });
    }, 1500);
  };

  /** Set the sentry user */
  public setUser({ id }: { id: string }) {
    Sentry.setUser({ id });
  }

  private async setHekateSubmitIdListener() {
    if (typeof window === 'undefined' || !window.hekateEventBus) return;
    window.hekateEventBus.addEventListener(
      'form-successfully-submitted',
      (evt: HekateEventBusCallbackParams<{ formDefinition: HekateFormSubmit }>) => {
        this.hekateSubmitId = evt.data.formDefinition.id;
      }
    );
  }
}

const errorReporter = new ErrorReporter();

if (!import.meta.server) {
  window.errorReporter = errorReporter;
}

export default errorReporter;
